import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { FloatingButtons } from '../components/FloatingButtons';
import { Button } from '../components/ui/button';
import { 
  Building2, 
  Stethoscope, 
  Warehouse, 
  Sparkles, 
  Snowflake, 
  Wrench,
  CheckCircle,
  Phone,
  Mail,
  ArrowRight,
  Target,
  Users,
  TrendingUp,
  Shield,
  Handshake,
  ClipboardCheck,
  MessageSquare,
  Clock,
  Award,
  Zap,
  Settings,
  Layers,
  BarChart3,
  Building,
  MapPin
} from 'lucide-react';

// Hero Section - Updated with better image
const HeroSection = () => {
  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center justify-center"
      data-testid="hero-section"
    >
      {/* Background Image - Modern commercial building */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1758448721149-aa0ce8e1b2c9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb21tZXJjaWFsJTIwYnVpbGRpbmclMjBpbnRlcmlvciUyMGxvYmJ5fGVufDB8fHx8MTc3Mjk5NDczOXww&ixlib=rb-4.1.0&q=85')`
        }}
      />
      {/* Overlay */}
      <div className="hero-overlay absolute inset-0" />
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6 animate-fade-in-up">
          Professionelle Gebäudereinigung<br className="hidden sm:block" /> für Unternehmen
        </h1>
        <p className="text-lg sm:text-xl text-slate-200 max-w-3xl mx-auto mb-10 animate-fade-in-up delay-100">
          KayaClean steht für zuverlässige, strukturierte und hochwertige Reinigungslösungen 
          für Büros, Praxen und große Gewerbeobjekte.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-200">
          <a href="#contact">
            <Button 
              className="bg-[#003366] hover:bg-[#002244] text-white px-8 py-4 h-14 text-lg font-semibold shadow-lg btn-lift w-full sm:w-auto rounded-sm"
              data-testid="hero-cta-primary"
            >
              Jetzt Anfrage stellen
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
          <a href="#services">
            <Button 
              variant="outline"
              className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#003366] px-8 py-4 h-14 text-lg font-semibold w-full sm:w-auto rounded-sm btn-lift"
              data-testid="hero-cta-secondary"
            >
              Unsere Leistungen
            </Button>
          </a>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-8 h-12 rounded-full border-2 border-white/50 flex items-start justify-center pt-2">
          <div className="w-1.5 h-3 bg-white/70 rounded-full" />
        </div>
      </div>
    </section>
  );
};

// About Section - Updated with professional cleaning image
const AboutSection = () => {
  const highlights = [
    'Effiziente Arbeitsabläufe',
    'Moderne Reinigungstechnik',
    'Geschultes Personal',
    'Langfristige Kundenbeziehungen'
  ];

  return (
    <section 
      id="about" 
      className="py-20 md:py-32 bg-white"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image - Updated to professional cleaning image */}
          <div className="relative">
            <img
              src="https://customer-assets.emergentagent.com/job_kayaclean-pro/artifacts/m33pxohx_IMG_3021.jpeg"
              alt="Professionelle Gebäudereinigung"
              className="w-full h-auto rounded-sm shadow-xl object-cover"
              style={{ maxHeight: '500px' }}
            />
            <div className="absolute -bottom-6 -right-6 bg-[#003366] text-white p-6 rounded-sm shadow-lg hidden md:block">
              <p className="text-3xl font-bold">Seit 2021</p>
              <p className="text-slate-300">im Markt tätig</p>
            </div>
          </div>

          {/* Content */}
          <div>
            <p className="text-sm font-medium tracking-widest uppercase text-[#00BFFF] mb-3">
              Über uns
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] tracking-tight mb-6">
              Über unser Unternehmen
            </h2>
            <div className="section-divider mb-8" />
            
            <p className="text-slate-600 text-lg leading-relaxed mb-6">
              KayaClean steht für zuverlässige und professionelle Gebäudereinigung. 
              Seit 2021 ist das Unternehmen erfolgreich im Markt tätig und betreut 
              Gewerbe- und Großkunden mit strukturierten Reinigungslösungen.
            </p>
            
            <p className="text-slate-600 leading-relaxed mb-8">
              Kunden können sich auf saubere Ergebnisse, transparente Kommunikation 
              und zuverlässige Umsetzung verlassen.
            </p>

            <div className="space-y-3">
              {highlights.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#00BFFF] flex-shrink-0" />
                  <span className="text-slate-700 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Building2,
      title: 'Büro- und Praxisreinigung',
      description: 'Professionelle Reinigung für Büros, Arztpraxen und Kanzleien. Hygienisch sauber und repräsentativ für Ihre Kunden und Mitarbeiter.'
    },
    {
      icon: Warehouse,
      title: 'Großobjekte & Gewerbeflächen',
      description: 'Reinigung von Shopping Centern, Industriegebäuden und großen Gewerbeflächen mit effizienten Prozessen.'
    },
    {
      icon: Sparkles,
      title: 'Glas- und Fassadenreinigung',
      description: 'Fachgerechte Reinigung von Glasflächen und Fassaden für einen strahlenden Eindruck Ihres Gebäudes.'
    },
    {
      icon: Snowflake,
      title: 'Winterdienst',
      description: 'Zuverlässiger Winterdienst für sichere Wege und Zufahrten. Schneeräumung und Streudienst nach Bedarf.'
    },
    {
      icon: Wrench,
      title: 'Hausmeisterservice',
      description: 'Umfassender Hausmeisterservice für Ihr Objekt. Regelmäßige Kontrollen und schnelle Reaktionszeiten.'
    },
    {
      icon: Stethoscope,
      title: 'Kleinreparaturen',
      description: 'Kleine Reparaturen und Instandhaltungsarbeiten aus einer Hand. Schnell, zuverlässig und kompetent.'
    }
  ];

  return (
    <section 
      id="services" 
      className="py-20 md:py-32 bg-[#F8FAFC]"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-sm font-medium tracking-widest uppercase text-[#00BFFF] mb-3">
            Unsere Leistungen
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#003366] tracking-tight mb-4">
            Professionelle Reinigungslösungen
          </h2>
          <div className="section-divider mx-auto mb-6" />
          <p className="text-slate-600 max-w-2xl mx-auto">
            Von der täglichen Büroreinigung bis zur Glasfassade – wir bieten das komplette 
            Spektrum professioneller Gebäudedienstleistungen.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div 
              key={index}
              className="service-card bg-white p-8 rounded-sm border border-slate-100 shadow-sm relative overflow-hidden group"
              data-testid={`service-card-${index}`}
            >
              {/* Accent line */}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#003366] to-[#00BFFF] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
              
              <div className="value-icon mb-6">
                <service.icon className="w-7 h-7 text-[#00BFFF]" />
              </div>
              
              <h3 className="text-xl font-semibold text-[#003366] mb-3">
                {service.title}
              </h3>
              
              <p className="text-slate-600 leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <a href="#contact">
            <Button 
              className="bg-[#003366] hover:bg-[#002244] text-white px-8 py-4 h-14 text-lg font-semibold shadow-lg btn-lift rounded-sm"
              data-testid="services-cta"
            >
              Kostenlose Beratung anfordern
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// References Section
const ReferencesSection = () => {
  const experiences = [
    'Große Gewerbeimmobilien',
    'Shopping Center',
    'Bürogebäude',
    'Industriegebäude'
  ];

  // Gallery images from client
  const galleryImages = [
    { src: 'https://customer-assets.emergentagent.com/job_kayaclean-pro/artifacts/6ykogp94_IMG_3019.jpeg', alt: 'Zahnarztpraxis Reinigung' },
    { src: 'https://customer-assets.emergentagent.com/job_kayaclean-pro/artifacts/oo818a1c_IMG_3025.jpeg', alt: 'Praxis Flur mit LED Beleuchtung' },
    { src: 'https://customer-assets.emergentagent.com/job_kayaclean-pro/artifacts/oshgaas3_IMG_3022.jpeg', alt: 'Baumarkt Bodenreinigung' },
    { src: 'https://customer-assets.emergentagent.com/job_kayaclean-pro/artifacts/4rvuapde_IMG_3024.jpeg', alt: 'Modernes Büro Reinigung' },
    { src: 'https://customer-assets.emergentagent.com/job_kayaclean-pro/artifacts/vzg71vkc_IMG_3018.jpeg', alt: 'Glasfassade Reinigung' },
    { src: 'https://customer-assets.emergentagent.com/job_kayaclean-pro/artifacts/c0woguyy_IMG_3026.jpeg', alt: 'Baumarkt Eingang' }
  ];

  return (
    <section 
      id="references" 
      className="py-20 md:py-32 bg-white"
      data-testid="references-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div>
            <p className="text-sm font-medium tracking-widest uppercase text-[#00BFFF] mb-3">
              Referenzen
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] tracking-tight mb-4">
              Referenzen & Projekte
            </h2>
            <div className="section-divider mb-8" />

            {/* Featured Project */}
            <div className="bg-[#F0F8FF] p-6 rounded-sm border-l-4 border-[#003366] mb-8">
              <h3 className="text-xl font-semibold text-[#003366] mb-2">
                Landesärztekammer Frankfurt
              </h3>
              <p className="text-slate-600 mb-4">
                Erfolgreich über 1.000 m² Glasflächen gereinigt. Ein Projekt, das unsere 
                Kompetenz in der professionellen Glasreinigung unter Beweis stellt.
              </p>
              <div className="flex items-center gap-2 text-[#00BFFF] font-semibold">
                <Sparkles className="w-5 h-5" />
                <span>1.000+ m² Glasflächen</span>
              </div>
            </div>

            <p className="text-slate-600 mb-6">
              Wir arbeiten regelmäßig mit großen Objekten und haben umfangreiche 
              Erfahrung in verschiedenen Branchen:
            </p>

            <div className="grid grid-cols-2 gap-4">
              {experiences.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#00BFFF] flex-shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Image */}
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1758448500717-2e4bcd79108b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBzaG9wcGluZyUyMG1hbGwlMjBpbnRlcmlvciUyMGJyaWdodHxlbnwwfHx8fDE3NzI5OTM1Mzl8MA&ixlib=rb-4.1.0&q=85"
              alt="Shopping Center Reinigung"
              className="w-full h-auto rounded-sm shadow-xl"
            />
          </div>
        </div>

        {/* Media Gallery Section - NEW */}
        <div className="mt-20 pt-16 border-t border-slate-200">
          <div className="text-center mb-12">
            <p className="text-sm font-medium tracking-widest uppercase text-[#00BFFF] mb-3">
              Galerie
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-[#003366] tracking-tight mb-4">
              Einblicke in unsere Arbeit
            </h3>
            <div className="section-divider mx-auto" />
          </div>

          {/* Image Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reference-gallery">
            {galleryImages.map((image, index) => (
              <div 
                key={index}
                className="group relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300"
                data-testid={`gallery-image-${index}`}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-64 object-cover transform group-hover:scale-105 transition-transform duration-300"
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-[#003366]/0 group-hover:bg-[#003366]/30 transition-all duration-300" />
              </div>
            ))}
          </div>

          {/* Video Section */}
          <div className="mt-16" data-testid="reference-video">
            <h4 className="text-xl font-semibold text-[#003366] mb-6 text-center">
              Projektvideo
            </h4>
            <div className="max-w-3xl mx-auto">
              <div className="relative rounded-lg overflow-hidden shadow-xl bg-slate-900">
                <video 
                  controls 
                  className="w-full aspect-video"
                  poster=""
                  preload="metadata"
                >
                  <source 
                    src="https://customer-assets.emergentagent.com/job_kayaclean-pro/artifacts/ci91pskz_e8800440-1cde-40c8-865c-24eeb027d8e6.mov" 
                    type="video/quicktime" 
                  />
                  <source 
                    src="https://customer-assets.emergentagent.com/job_kayaclean-pro/artifacts/ci91pskz_e8800440-1cde-40c8-865c-24eeb027d8e6.mov" 
                    type="video/mp4" 
                  />
                  Ihr Browser unterstützt das Video-Format nicht.
                </video>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// NEW: Leistungen am Markt Statistics Section - Redesigned to match Zahlen & Erfahrung style
const MarketStatsSection = () => {
  const stats = [
    { value: '2.5', unit: 'M m²', label: 'Flächen gereinigt', description: 'Wir reinigen jährlich über 2,5 Millionen Quadratmeter in verschiedenen Objekten – Tendenz steigend.' },
    { value: '3.5', unit: 'K Stunden', label: 'Monatliche Arbeit', description: 'Unsere Teams leisten monatlich über 3.500 Stunden professionelle Reinigungsarbeit mit höchster Präzision.' },
    { value: '98', unit: '%', label: 'Kundenzufriedenheit', description: '98 % unserer Auftraggeber bleiben dauerhaft bei uns – ein Beweis für Qualität, Transparenz und Verlässlichkeit.' }
  ];

  return (
    <section 
      id="market-stats" 
      className="py-20 md:py-24 bg-[#003366]"
      data-testid="market-stats-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
            Unsere Leistungen am Markt
          </h2>
          <p className="text-slate-300 max-w-3xl mx-auto">
            Wir betreuen gewerbliche Kunden mit konstant hohen Flächenleistungen und zuverlässigen 
            Reinigungsabläufen. Unsere Stärke: effiziente Strukturen, starke Teams und planbare Qualität.
          </p>
        </div>

        {/* Statistics Grid - Same style as Zahlen & Erfahrung */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {stats.map((stat, index) => (
            <div 
              key={index}
              className="text-center"
              data-testid={`market-stat-${index}`}
            >
              <div className="stat-number text-4xl sm:text-5xl lg:text-6xl font-bold text-[#00BFFF] mb-2">
                {stat.value}<span className="text-2xl sm:text-3xl">{stat.unit}</span>
              </div>
              <p className="text-white font-medium">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Statistics Descriptions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {stats.map((stat, index) => (
            <div 
              key={index}
              className="bg-white/10 backdrop-blur-sm p-6 rounded-sm border border-white/20"
            >
              <div className="w-12 h-12 bg-[#00BFFF]/20 rounded-sm flex items-center justify-center mb-4">
                {index === 0 && <Layers className="w-6 h-6 text-[#00BFFF]" />}
                {index === 1 && <Clock className="w-6 h-6 text-[#00BFFF]" />}
                {index === 2 && <Users className="w-6 h-6 text-[#00BFFF]" />}
              </div>
              <p className="text-slate-200 leading-relaxed text-sm">
                {stat.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Stats Section (Original)
const StatsSection = () => {
  const stats = [
    { value: '1.5', unit: 'Mio m²', label: 'Bauflächen betreut', year: '2024' },
    { value: '1', unit: 'Mio m²', label: 'Glasflächen gereinigt', year: '2024' },
    { value: '75+', unit: '', label: 'Zufriedene Kunden', year: '' },
    { value: '5+', unit: '', label: 'Jahre Erfahrung', year: '' }
  ];

  return (
    <section 
      id="stats" 
      className="py-20 md:py-24 bg-[#003366]"
      data-testid="stats-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
            Zahlen & Erfahrung
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto">
            Wachsende Kundenbasis, steigende Kapazitäten und starkes Unternehmenswachstum.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div 
              key={index}
              className="text-center"
              data-testid={`stat-${index}`}
            >
              <div className="stat-number text-4xl sm:text-5xl lg:text-6xl font-bold text-[#00BFFF] mb-2">
                {stat.value}<span className="text-2xl sm:text-3xl">{stat.unit}</span>
              </div>
              <p className="text-white font-medium">{stat.label}</p>
              {stat.year && (
                <p className="text-slate-400 text-sm mt-1">{stat.year}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Values Section - Updated to 6 blocks
const ValuesSection = () => {
  const values = [
    { icon: Shield, title: 'Zuverlässigkeit', description: 'Pünktlich und verlässlich – auf uns können Sie zählen.' },
    { icon: Target, title: 'Hohe Qualitätsstandards', description: 'Höchste Ansprüche an unsere Arbeit und Ergebnisse.' },
    { icon: ClipboardCheck, title: 'Strukturierte Abläufe', description: 'Effiziente Prozesse für optimale Ergebnisse.' },
    { icon: MessageSquare, title: 'Transparente Kommunikation', description: 'Offener Dialog und klare Absprachen.' },
    { icon: Handshake, title: 'Langfristige Zusammenarbeit', description: 'Partnerschaftliche Beziehungen zu unseren Kunden.' },
    { icon: Award, title: 'Erfahrung & Kompetenz', description: 'Jahrelange Erfahrung und fachliches Know-how.' }
  ];

  return (
    <section 
      id="values" 
      className="py-20 md:py-32 bg-[#F8FAFC]"
      data-testid="values-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-sm font-medium tracking-widest uppercase text-[#00BFFF] mb-3">
            Unsere Werte
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#003366] tracking-tight mb-4">
            Firmenwerte
          </h2>
          <div className="section-divider mx-auto mb-6" />
          <p className="text-slate-600 max-w-2xl mx-auto">
            KayaClean setzt auf konstante Qualität und professionelle Umsetzung.
          </p>
        </div>

        {/* 6 blocks in 3x2 grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {values.map((value, index) => (
            <div 
              key={index}
              className="bg-white p-8 rounded-sm border border-slate-100 shadow-sm flex items-start gap-5"
              data-testid={`value-${index}`}
            >
              <div className="value-icon flex-shrink-0">
                <value.icon className="w-6 h-6 text-[#00BFFF]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#003366] mb-2">
                  {value.title}
                </h3>
                <p className="text-slate-600">
                  {value.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// NEW: Firmen Vision Section
const VisionSection = () => {
  const visionPoints = [
    {
      icon: Zap,
      title: 'Full-Service Anbieter',
      description: 'Wir wachsen zum Full-Service Anbieter. Reinigung, Hausmeisterservice und Winterdienst – alles aus einer Hand.'
    },
    {
      icon: Settings,
      title: 'Reparaturen & Renovierungen',
      description: 'Kleine Reparaturen bis Renovierungen. Wir kümmern uns um alles, was im Objekt anfällt.'
    },
    {
      icon: Snowflake,
      title: 'Winterdienst Expertise',
      description: 'Über 60 betreute Objekte pro Winter – zuverlässig, schnell und bestens organisiert.'
    }
  ];

  return (
    <section 
      id="vision" 
      className="py-20 md:py-32 bg-white"
      data-testid="vision-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-sm font-medium tracking-widest uppercase text-[#00BFFF] mb-3">
            Vision
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#003366] tracking-tight mb-4">
            Unsere Firmen Vision
          </h2>
          <div className="section-divider mx-auto" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text blocks */}
          <div className="space-y-8">
            {visionPoints.map((point, index) => (
              <div 
                key={index}
                className="flex items-start gap-5 p-6 bg-[#F8FAFC] rounded-sm border-l-4 border-[#00BFFF]"
                data-testid={`vision-point-${index}`}
              >
                <div className="w-12 h-12 bg-[#003366] rounded-sm flex items-center justify-center flex-shrink-0">
                  <point.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#003366] mb-2">
                    {point.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {point.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Image - Winterdienst - Updated with new professional image */}
          <div className="relative">
            <img
              src="https://customer-assets.emergentagent.com/job_kayaclean-pro/artifacts/kh1hovud_ChatGPT%20Image%208.%20M%C3%A4rz%202026%2C%2019_48_02.png"
              alt="Winterdienst - Schneeräumung vor Gebäude"
              className="w-full h-auto rounded-sm shadow-xl object-cover"
              style={{ maxHeight: '500px' }}
            />
            <div className="absolute -bottom-6 -left-6 bg-[#00BFFF] text-white p-5 rounded-sm shadow-lg hidden md:block">
              <p className="text-2xl font-bold">60+</p>
              <p className="text-sm">Objekte im Winter</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Future Section - Improved Design with Frankfurt Skyline
const FutureSection = () => {
  return (
    <section 
      id="future" 
      className="py-20 md:py-32 bg-[#F8FAFC]"
      data-testid="future-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-sm font-medium tracking-widest uppercase text-[#00BFFF] mb-3">
            Zukunft
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#003366] tracking-tight mb-4">
            Unsere zukünftigen Projekte
          </h2>
          <div className="section-divider mx-auto" />
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Image - Updated with provided Frankfurt Skyline */}
          <div className="relative order-2 lg:order-1">
            <div className="relative rounded-lg overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_kayaclean-pro/artifacts/8hjipyj1_ChatGPT%20Image%208.%20M%C3%A4rz%202026%2C%2019_49_06.png"
                alt="Frankfurt Skyline - Expansion"
                className="w-full h-auto object-cover"
                style={{ minHeight: '400px', maxHeight: '500px' }}
              />
              {/* Overlay with growth icon */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#003366]/80 via-transparent to-transparent flex items-end p-8">
                <div className="text-white">
                  <TrendingUp className="w-10 h-10 mb-2" />
                  <p className="text-xl font-semibold">Region Frankfurt</p>
                  <p className="text-slate-200 text-sm">Unser Expansionsziel</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Content - Modern Card Design */}
          <div className="order-1 lg:order-2">
            <div className="bg-white rounded-lg shadow-xl p-8 md:p-10 border border-slate-200">
              {/* Growth Icon */}
              <div className="w-14 h-14 bg-[#F0F8FF] rounded-lg flex items-center justify-center mb-6">
                <BarChart3 className="w-7 h-7 text-[#00BFFF]" />
              </div>

              <p className="text-slate-700 text-lg leading-relaxed mb-6">
                KayaClean plant, weiter in die Region Frankfurt zu expandieren. 
                Unser Ziel ist es, im Bereich der gewerblichen Reinigung weiter zu wachsen 
                und größere Projekte zu übernehmen.
              </p>
              
              <p className="text-slate-600 leading-relaxed mb-8">
                Mit unserem wachsenden Team und modernster Ausstattung sind wir bereit, 
                neue Herausforderungen anzunehmen und unseren Kunden noch besseren Service zu bieten.
              </p>

              {/* Key Points */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-3 bg-[#F8FAFC] p-3 rounded-lg">
                  <div className="w-10 h-10 bg-[#F0F8FF] rounded-lg flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#00BFFF]" />
                  </div>
                  <span className="text-slate-700 font-medium">Region Frankfurt</span>
                </div>
                <div className="flex items-center gap-3 bg-[#F8FAFC] p-3 rounded-lg">
                  <div className="w-10 h-10 bg-[#F0F8FF] rounded-lg flex items-center justify-center">
                    <Building className="w-5 h-5 text-[#00BFFF]" />
                  </div>
                  <span className="text-slate-700 font-medium">Große Projekte</span>
                </div>
              </div>

              <a href="#contact">
                <Button 
                  className="w-full sm:w-auto bg-[#003366] hover:bg-[#002244] text-white px-10 py-4 h-14 text-lg font-semibold shadow-lg btn-lift rounded-sm"
                  data-testid="future-cta"
                >
                  Werden Sie unser Partner
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section - Clean, Direct Contact Options (No Form)
const ContactSection = () => {
  const phoneNumber = "+4916092653278";
  const email = "fa.kayaclean@gmail.com";
  const whatsappMessage = encodeURIComponent("Hallo, ich interessiere mich für Ihre Reinigungsleistungen und hätte gerne ein Angebot.");

  return (
    <section 
      id="contact" 
      className="py-20 md:py-32 bg-white"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-sm font-medium tracking-widest uppercase text-[#00BFFF] mb-3">
            Kontakt
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#003366] tracking-tight mb-4">
            So erreichen Sie uns
          </h2>
          <div className="section-divider mx-auto mb-6" />
          <p className="text-slate-600 max-w-2xl mx-auto text-lg">
            Kontaktieren Sie uns schnell und unkompliziert – wählen Sie einfach den passenden Weg:
          </p>
        </div>

        {/* Contact Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto mb-16">
          
          {/* Phone Card */}
          <a
            href={`tel:${phoneNumber}`}
            className="group bg-white p-8 rounded-lg border-2 border-slate-100 shadow-lg hover:shadow-xl hover:border-[#003366] transition-all duration-300 text-center"
            data-testid="contact-phone-card"
          >
            <div className="w-20 h-20 bg-[#003366] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
              <Phone className="w-9 h-9 text-white" />
            </div>
            <h3 className="text-xl font-bold text-[#003366] mb-2">Anrufen</h3>
            <p className="text-slate-500 text-sm mb-4">Direkt mit uns sprechen</p>
            <p className="text-xl font-bold text-[#003366] group-hover:text-[#00BFFF] transition-colors">
              +49 160 92653278
            </p>
          </a>

          {/* Email Card */}
          <a
            href={`mailto:${email}?subject=Anfrage%20über%20Website`}
            className="group bg-white p-8 rounded-lg border-2 border-slate-100 shadow-lg hover:shadow-xl hover:border-[#003366] transition-all duration-300 text-center"
            data-testid="contact-email-card"
          >
            <div className="w-20 h-20 bg-[#003366] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
              <Mail className="w-9 h-9 text-white" />
            </div>
            <h3 className="text-xl font-bold text-[#003366] mb-2">E-Mail schreiben</h3>
            <p className="text-slate-500 text-sm mb-4">Wir antworten schnell</p>
            <p className="text-lg font-bold text-[#003366] group-hover:text-[#00BFFF] transition-colors break-all">
              {email}
            </p>
          </a>

          {/* WhatsApp Card */}
          <a
            href={`https://wa.me/${phoneNumber.replace('+', '')}?text=${whatsappMessage}`}
            target="_blank"
            rel="noopener noreferrer"
            className="group bg-white p-8 rounded-lg border-2 border-slate-100 shadow-lg hover:shadow-xl hover:border-[#25D366] transition-all duration-300 text-center"
            data-testid="contact-whatsapp-card"
          >
            <div className="w-20 h-20 bg-[#25D366] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
              <MessageSquare className="w-9 h-9 text-white" />
            </div>
            <h3 className="text-xl font-bold text-[#003366] mb-2">WhatsApp</h3>
            <p className="text-slate-500 text-sm mb-4">Chat starten</p>
            <p className="text-lg font-bold text-[#25D366]">
              Jetzt schreiben
            </p>
          </a>
        </div>

        {/* Contact Info Box */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-[#003366] text-white p-8 md:p-10 rounded-lg shadow-xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Left - Contact Person */}
              <div>
                <h3 className="text-xl font-bold mb-4 flex items-center gap-3">
                  <Users className="w-6 h-6 text-[#00BFFF]" />
                  Ihr Ansprechpartner
                </h3>
                <p className="text-2xl font-bold mb-2">Sinan Kaya</p>
                <p className="text-slate-300">Geschäftsführer</p>
                <p className="text-slate-300">KayaClean Gebäudedienstleister</p>
              </div>

              {/* Right - Response Time */}
              <div>
                <h3 className="text-xl font-bold mb-4 flex items-center gap-3">
                  <Clock className="w-6 h-6 text-[#00BFFF]" />
                  Schnelle Reaktion
                </h3>
                <p className="text-slate-200 leading-relaxed">
                  Wir melden uns in der Regel innerhalb von <strong className="text-white">24 Stunden</strong> bei Ihnen zurück.
                </p>
                <p className="text-slate-300 mt-3 text-sm">
                  Für dringende Anfragen rufen Sie uns einfach an!
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Address Info */}
        <div className="max-w-3xl mx-auto mt-8">
          <div className="bg-[#F8FAFC] p-6 md:p-8 rounded-lg border border-slate-200 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <MapPin className="w-6 h-6 text-[#00BFFF]" />
              <h4 className="text-lg font-semibold text-[#003366]">Unser Standort</h4>
            </div>
            <p className="text-slate-600">
              Am Schwalbengraben 17 · 35584 Wetzlar · Deutschland
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Main Home Page - Updated section order
export const Home = () => {
  return (
    <div className="min-h-screen" data-testid="home-page">
      <Navigation />
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <ReferencesSection />
      <MarketStatsSection />
      <StatsSection />
      <ValuesSection />
      <VisionSection />
      <FutureSection />
      <ContactSection />
      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default Home;
